import { useState, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Edit2, Trash2, ShoppingBag, ExternalLink, Check, X,
  Plus, Loader2, Layers, RefreshCw, Link2, Search, Shirt,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { useWardrobeStore } from '../../stores/wardrobeStore';
import { garmentService } from '../../services/garmentService';
import { compressImages } from '../../utils/imageCompression';
import { useGarmentImage, useGarmentImages } from '@/hooks/useGarmentImage';
import type { Garment, GarmentCategory, GarmentPattern, GarmentMaterial } from '../../types';

const CATEGORY_OPTIONS: GarmentCategory[] = ['top', 'bottom', 'dress', 'outerwear', 'shoes', 'accessory', 'other'];
const PATTERN_OPTIONS: GarmentPattern[] = ['solid', 'stripes', 'plaid', 'floral', 'polka-dot', 'geometric', 'abstract', 'animal-print', 'other'];
const MATERIAL_OPTIONS: GarmentMaterial[] = ['cotton', 'denim', 'silk', 'wool', 'polyester', 'leather', 'linen', 'cashmere', 'nylon', 'velvet', 'chiffon', 'satin', 'knit', 'other'];

interface Props { garment?: Garment; onBack?: () => void; }

export function GarmentDetail({ garment: garmentProp, onBack }: Props) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const garments = useWardrobeStore((s) => s.garments);
  const garment = garmentProp ?? garments.find((g) => g.id === id);

  const handleBack = onBack ?? (() => navigate('/closet'));

  if (!garment) {
    return <div className="flex items-center justify-center h-64 text-gray-400">No garment selected</div>;
  }

  const [activeTab, setActiveTab] = useState<'details' | 'segments' | 'similar' | 'purchase'>('details');
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [linkInput, setLinkInput] = useState('');
  const [isAddingLink, setIsAddingLink] = useState(false);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [similarGarments, setSimilarGarments] = useState<Garment[]>([]);
  const [isLoadingSimilar, setIsLoadingSimilar] = useState(false);
  const [editData, setEditData] = useState({
    category: garment.category,
    pattern: garment.pattern,
    material: garment.material,
    size: garment.size || '',
    brand: garment.brand || '',
  });

  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { updateGarment, removeGarment } = useWardrobeStore();

  // Hero image = first image (product photo), segments = rest
  const heroImageRaw = garment.imageUrls?.[0];
  const heroImage = useGarmentImage(heroImageRaw);
  const segmentImagesRaw = garment.imageUrls?.slice(1) || [];
  const segmentImages = useGarmentImages(segmentImagesRaw);

  const handleSaveEdit = async () => {
    await updateGarment(garment.id, editData);
    setIsEditing(false);
    toast.success('Garment updated');
  };

  const handleDeleteClick = useCallback(() => {
    setShowDeleteConfirm(true);
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }, []);

  const handleDelete = useCallback(async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      await removeGarment(garment.id);
      handleBack();
    } catch {
      setIsDeleting(false);
    }
  }, [isDeleting, removeGarment, garment.id, handleBack]);

  const handleAddPhotos = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setIsUploadingPhotos(true);
    try {
      const compressed = await compressImages(Array.from(files));
      if (garment.id.startsWith('garment-')) {
        // Local garment - convert files to data URLs and update store directly
        const newUrls: string[] = [];
        for (const file of compressed) {
          const dataUrl = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.readAsDataURL(file);
          });
          newUrls.push(dataUrl);
        }
        const currentUrls = garment.imageUrls || [];
        await updateGarment(garment.id, { imageUrls: [...currentUrls, ...newUrls] });
        toast.success(`${files.length} photo${files.length > 1 ? 's' : ''} added`);
      } else {
        await garmentService.addImages(garment.id, compressed);
        toast.success(`${files.length} photo${files.length > 1 ? 's' : ''} added`);
      }
    } catch {
      toast.error('Failed to upload photos');
    } finally {
      setIsUploadingPhotos(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [garment.id, garment.imageUrls, updateGarment]);

  const handleRemoveSegment = useCallback(async (imageUrl: string) => {
    try {
      if (garment.id.startsWith('garment-')) {
        // Local garment - filter out the image URL
        const currentUrls = garment.imageUrls || [];
        const idx = currentUrls.indexOf(imageUrl);
        // Also check idb:// refs
        const updatedUrls = currentUrls.filter((u, i) => u !== imageUrl && i !== currentUrls.indexOf(imageUrl));
        await updateGarment(garment.id, { imageUrls: updatedUrls.length > 0 ? updatedUrls : undefined });
        toast.success('Segment removed');
      } else {
        await garmentService.removeImage(garment.id, imageUrl);
        toast.success('Segment removed');
      }
    } catch {
      toast.error('Failed to remove segment');
    }
  }, [garment.id, garment.imageUrls, updateGarment]);

  const handleRegenerate = useCallback(async () => {
    if (isRegenerating) return;
    setIsRegenerating(true);
    try {
      // This would trigger server-side re-generation of the product photo
      // from the current segments. The backend handles SDXL-Turbo call.
      await garmentService.update(garment.id, { notes: '__regenerate_product_photo__' });
      toast.success('Product photo regenerating…');
    } catch {
      toast.error('Regeneration failed');
    } finally {
      setIsRegenerating(false);
    }
  }, [garment.id, isRegenerating]);

  const handleAddLink = useCallback(async () => {
    if (!linkInput.trim()) return;
    setIsAddingLink(true);
    try {
      // Backend scrapes the URL and adds product photos as segments
      await garmentService.update(garment.id, {
        purchaseLinks: [
          ...(garment.purchaseLinks || []),
          { id: `link-${Date.now()}`, retailer: new URL(linkInput).hostname, url: linkInput, price: 0, currency: 'USD', inStock: true, sizes: [], recommendedSize: null, lastChecked: new Date().toISOString() },
        ],
      } as any);
      toast.success('Link added - scraping product photos…');
      setLinkInput('');
      setShowLinkInput(false);
    } catch {
      toast.error('Failed to add link');
    } finally {
      setIsAddingLink(false);
    }
  }, [garment.id, garment.purchaseLinks, linkInput]);

  const handleLoadSimilar = useCallback(async () => {
    if (isLoadingSimilar) return;
    setIsLoadingSimilar(true);
    try {
      const results = await garmentService.searchSimilar(garment.id);
      setSimilarGarments(results);
    } catch {
      toast.error('Failed to load similar items');
    } finally {
      setIsLoadingSimilar(false);
    }
  }, [garment.id, isLoadingSimilar]);

  return (
    <div ref={scrollRef} className="flex flex-col h-full bg-[#FAFAF8] overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-center gap-3 px-4 py-3 bg-[#FAFAF8]/95 backdrop-blur-sm">
        <button onClick={handleBack} className="p-2 rounded-lg hover:bg-gray-100"><ArrowLeft size={20} /></button>
        <h2 className="flex-1 font-semibold truncate">{garment.name || garment.subCategory}</h2>
        {isEditing ? (
          <>
            <button onClick={handleSaveEdit} className="p-2 rounded-lg hover:bg-green-50 text-green-600"><Check size={18} /></button>
            <button onClick={() => setIsEditing(false)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"><X size={18} /></button>
          </>
        ) : (
          <>
            <button onClick={() => setIsEditing(true)} className="p-2 rounded-lg hover:bg-gray-100"><Edit2 size={18} /></button>
            <button onClick={handleDeleteClick} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={18} /></button>
          </>
        )}
      </div>

      {/* Delete confirmation */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="mx-4 mb-3 p-4 bg-red-50 rounded-xl border border-red-200">
              <p className="text-sm font-medium text-red-800 mb-3">Remove this garment from your closet?</p>
              <div className="flex gap-2">
                <button onClick={handleDelete} disabled={isDeleting}
                  className="flex-1 py-2 rounded-lg bg-red-600 text-white text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2">
                  {isDeleting ? <><Loader2 size={14} className="animate-spin" /> Deleting...</> : 'Delete'}
                </button>
                <button onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting}
                  className="flex-1 py-2 rounded-lg bg-white border border-gray-200 text-sm font-medium disabled:opacity-50">
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero image (product photo) */}
      <div className="relative mx-4 rounded-2xl overflow-hidden">
        <div className="relative aspect-square bg-white">
          {heroImage ? (
            <img src={heroImage} alt={garment.name} className="w-full h-full object-contain" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-300">
              <Shirt size={48} />
            </div>
          )}
        </div>

        {/* Regenerate button */}
        <div className="absolute bottom-3 right-3 flex gap-2">
          <button
            onClick={handleRegenerate}
            disabled={isRegenerating}
            className="flex items-center gap-1.5 px-3 py-2 bg-[#6C3CE1] text-white rounded-full text-xs font-medium shadow-lg hover:bg-[#5A2FCF] transition-colors disabled:opacity-50"
          >
            {isRegenerating ? (
              <><Loader2 size={14} className="animate-spin" /> Regenerating…</>
            ) : (
              <><RefreshCw size={14} /> Regenerate</>
            )}
          </button>
        </div>
      </div>

      {/* Try On CTA */}
      <div className="px-4 mt-3">
        <button
          onClick={() => navigate('/tryon', { state: { preselectedGarment: garment } })}
          className="w-full py-3 rounded-xl bg-gradient-to-r from-[#6C3CE1] to-[#9B59B6] text-white font-medium flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-shadow"
        >
          <Shirt size={18} /> Try On
        </button>
      </div>

      {/* Tabs */}
      <div className="flex px-4 mt-4 gap-1">
        {(['details', 'segments', 'similar', 'purchase'] as const).map(tab => (
          <button key={tab} onClick={() => {
            setActiveTab(tab);
            if (tab === 'similar' && similarGarments.length === 0) handleLoadSimilar();
          }}
            className={`flex-1 py-2 text-xs font-medium rounded-lg capitalize transition-colors ${
              activeTab === tab ? 'bg-[#6C3CE1] text-white' : 'bg-white text-gray-600'
            }`}>
            {tab}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 px-4 py-4 pb-24">
        <AnimatePresence mode="wait">
          {/* Details Tab */}
          {activeTab === 'details' && (
            <motion.div key="details" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
              {isEditing ? (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-gray-500">Category</label>
                    <select value={editData.category} onChange={e => setEditData(d => ({ ...d, category: e.target.value as GarmentCategory }))}
                      className="w-full mt-1 p-2.5 rounded-xl border border-gray-200 text-sm capitalize">
                      {CATEGORY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Pattern</label>
                    <select value={editData.pattern} onChange={e => setEditData(d => ({ ...d, pattern: e.target.value as GarmentPattern }))}
                      className="w-full mt-1 p-2.5 rounded-xl border border-gray-200 text-sm capitalize">
                      {PATTERN_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Material</label>
                    <select value={editData.material} onChange={e => setEditData(d => ({ ...d, material: e.target.value as GarmentMaterial }))}
                      className="w-full mt-1 p-2.5 rounded-xl border border-gray-200 text-sm capitalize">
                      {MATERIAL_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-500">Size</label>
                      <input value={editData.size} onChange={e => setEditData(d => ({ ...d, size: e.target.value }))}
                        placeholder="M, L, 10..." className="w-full mt-1 p-2.5 rounded-xl border border-gray-200 text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Brand</label>
                      <input value={editData.brand} onChange={e => setEditData(d => ({ ...d, brand: e.target.value }))}
                        placeholder="Optional" className="w-full mt-1 p-2.5 rounded-xl border border-gray-200 text-sm" />
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      ['Category', garment.category],
                      ['Sub-category', garment.subCategory],
                      ['Pattern', garment.pattern],
                      ['Material', garment.material],
                      ['Size', garment.size || '-'],
                      ['Brand', garment.brand || '-'],
                      ['Times Worn', String(garment.timesWorn ?? 0)],
                      ['Price', garment.purchasePrice ? `$${garment.purchasePrice}` : '-'],
                    ].map(([label, value]) => (
                      <div key={label} className="bg-white p-3 rounded-xl">
                        <p className="text-xs text-gray-400">{label}</p>
                        <p className="text-sm font-medium capitalize">{value}</p>
                      </div>
                    ))}
                  </div>
                  <div className="bg-white p-3 rounded-xl">
                    <p className="text-xs text-gray-400 mb-2">Colors</p>
                    <div className="flex gap-2 flex-wrap">
                      {garment.colors?.map((color, i) => {
                        // Support both string colors and {hex, name, percentage} objects
                        const hex = typeof color === 'string' ? color : (color as any)?.hex || '#ccc';
                        const label = typeof color === 'string' ? color : (color as any)?.name || hex;
                        return (
                          <div key={i} className="flex items-center gap-1.5">
                            <div className="w-5 h-5 rounded-full border" style={{ backgroundColor: hex }} />
                            <span className="text-xs capitalize">{label}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded-xl">
                    <p className="text-xs text-gray-400 mb-2">Seasons</p>
                    <div className="flex gap-2 flex-wrap">
                      {garment.seasons?.map((season) => (
                        <span key={season} className="px-2 py-1 bg-[#6C3CE1]/10 text-[#6C3CE1] rounded-lg text-xs capitalize">
                          {season}
                        </span>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </motion.div>
          )}

          {/* Segments Tab - shows all source segmentations */}
          {activeTab === 'segments' && (
            <motion.div key="segments" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">Source Segmentations</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Add or remove photos to refine the product image
                  </p>
                </div>
                <span className="text-xs text-gray-400">{segmentImages.length} segment{segmentImages.length !== 1 ? 's' : ''}</span>
              </div>

              {/* Segment grid */}
              <div className="grid grid-cols-3 gap-2">
                {segmentImages.map((url, i) => (
                  <div key={i} className="relative group">
                    <div
                      className="aspect-square rounded-xl overflow-hidden"
                      style={{
                        backgroundImage: 'linear-gradient(45deg, #f0f0f0 25%, transparent 25%), linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f0f0 75%), linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)',
                        backgroundSize: '12px 12px',
                        backgroundPosition: '0 0, 0 6px, 6px -6px, -6px 0px',
                        backgroundColor: '#fafafa',
                      }}
                    >
                      <img src={url} alt={`Segment ${i + 1}`} className="w-full h-full object-contain" />
                    </div>
                    {/* Remove button */}
                    <button
                      onClick={() => handleRemoveSegment(url)}
                      className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                    >
                      <X size={12} />
                    </button>
                    <p className="text-[10px] text-gray-400 text-center mt-1">Segment {i + 1}</p>
                  </div>
                ))}

                {/* Add photo button */}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingPhotos}
                  className="aspect-square rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center hover:border-[#6C3CE1] hover:bg-[#6C3CE1]/5 transition-colors disabled:opacity-50"
                >
                  {isUploadingPhotos ? (
                    <Loader2 size={20} className="text-[#6C3CE1] animate-spin" />
                  ) : (
                    <>
                      <Plus size={20} className="text-gray-400" />
                      <span className="text-[10px] text-gray-400 mt-1">Add Photo</span>
                    </>
                  )}
                </button>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => handleAddPhotos(e.target.files)}
              />

              {/* Attach link */}
              <div className="bg-white rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Link2 size={16} className="text-[#6C3CE1]" />
                    <p className="text-sm font-medium text-gray-700">Attach Product Link</p>
                  </div>
                  <button
                    onClick={() => setShowLinkInput(!showLinkInput)}
                    className="text-xs text-[#6C3CE1] font-medium"
                  >
                    {showLinkInput ? 'Cancel' : 'Add'}
                  </button>
                </div>
                <p className="text-xs text-gray-400">
                  Paste a link to the product page - we'll scrape the official photos
                </p>

                <AnimatePresence>
                  {showLinkInput && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="flex gap-2"
                    >
                      <input
                        value={linkInput}
                        onChange={(e) => setLinkInput(e.target.value)}
                        placeholder="https://zara.com/..."
                        className="flex-1 p-2.5 rounded-xl border border-gray-200 text-sm"
                      />
                      <button
                        onClick={handleAddLink}
                        disabled={isAddingLink || !linkInput.trim()}
                        className="px-4 py-2.5 rounded-xl bg-[#6C3CE1] text-white text-sm font-medium disabled:opacity-40"
                      >
                        {isAddingLink ? <Loader2 size={14} className="animate-spin" /> : 'Add'}
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Existing links */}
                {garment.purchaseLinks?.length > 0 && (
                  <div className="space-y-2 pt-1">
                    {garment.purchaseLinks.map((link, i) => (
                      <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                        <ExternalLink size={14} className="text-gray-400 flex-shrink-0" />
                        <span className="text-xs text-gray-600 truncate">{link.retailer}</span>
                        {link.price > 0 && <span className="text-xs text-gray-400 ml-auto">{link.currency} {link.price}</span>}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Regenerate CTA */}
              <button
                onClick={handleRegenerate}
                disabled={isRegenerating}
                className="w-full py-3 rounded-xl border-2 border-[#6C3CE1] text-[#6C3CE1] font-medium flex items-center justify-center gap-2 hover:bg-[#6C3CE1]/5 transition-colors disabled:opacity-50"
              >
                {isRegenerating ? (
                  <><Loader2 size={16} className="animate-spin" /> Regenerating…</>
                ) : (
                  <><RefreshCw size={16} /> Regenerate Product Photo</>
                )}
              </button>
            </motion.div>
          )}

          {/* Similar Tab */}
          {activeTab === 'similar' && (
            <motion.div key="similar" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-700">Similar Items</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  Clothes that look like this from the web and your closet
                </p>
              </div>

              {isLoadingSimilar ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 size={24} className="text-[#6C3CE1] animate-spin" />
                </div>
              ) : similarGarments.length > 0 ? (
                <div className="grid grid-cols-2 gap-3">
                  {similarGarments.map((sg) => (
                    <div key={sg.id} className="bg-white rounded-xl overflow-hidden shadow-sm">
                      <div className="aspect-square bg-gray-100">
                        {sg.thumbnailUrl ? (
                          <img src={sg.thumbnailUrl} alt={sg.name} className="w-full h-full object-contain" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-300">
                            <Shirt size={24} />
                          </div>
                        )}
                      </div>
                      <div className="p-2">
                        <p className="text-xs font-medium text-gray-700 truncate capitalize">{sg.subCategory || sg.category}</p>
                        {sg.brand && <p className="text-[10px] text-gray-400">{sg.brand}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Search size={32} className="mx-auto text-gray-300 mb-3" />
                  <p className="text-sm text-gray-400">No similar items found yet</p>
                  <button
                    onClick={handleLoadSimilar}
                    className="mt-3 text-sm text-[#6C3CE1] font-medium"
                  >
                    Search again
                  </button>
                </div>
              )}
            </motion.div>
          )}

          {/* Purchase Tab */}
          {activeTab === 'purchase' && (
            <motion.div key="purchase" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-3">
              {garment.purchaseLinks?.length ? garment.purchaseLinks.map((link, i) => (
                <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 bg-white p-4 rounded-xl hover:shadow-md transition-shadow">
                  <ShoppingBag size={18} className="text-[#6C3CE1]" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{link.retailer}</p>
                    {link.price > 0 && <p className="text-xs text-gray-400">{link.currency} {link.price}</p>}
                  </div>
                  <ExternalLink size={16} className="text-gray-400" />
                </a>
              )) : (
                <div className="text-center py-12">
                  <ShoppingBag size={32} className="mx-auto text-gray-300 mb-3" />
                  <p className="text-sm text-gray-400">No purchase links yet</p>
                  <button
                    onClick={() => { setActiveTab('segments'); setShowLinkInput(true); }}
                    className="mt-3 text-sm text-[#6C3CE1] font-medium"
                  >
                    Add a product link
                  </button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
